import { LanguagePicker } from '@/components/common/LanguagePicker';
import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/components/common/Toast';
import { AdminSidebar, AdminNavItem } from '@/components/layout/AdminSidebar';
import {
  Map,
  Zap,
  MapPin,
  Menu,
  ChevronRight,
  Building2,
  Network,
  Workflow,
  Route as RouteIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // People, roles and seats are in the user management service; what is here is GeoNexora's own (docs/en/60).
  // Companies bring their own files into their workspaces, from the map (My workspaces), not here.
  const navItems: AdminNavItem[] = [
    { path: '/admin', label: t('admin.layers'), icon: <Map size={20} /> },
    { path: '/admin/companies', label: t('companies.title'), icon: <Building2 size={20} /> },
    { path: '/admin/cache', label: t('admin.cache'), icon: <Zap size={20} /> },
    { path: '/admin/routing', label: t('admin.routing'), icon: <RouteIcon size={20} /> },
    { path: '/admin/taxonomy', label: t('admin.taxonomy'), icon: <Network size={20} /> },
    { path: '/admin/flows', label: t('admin.flows'), icon: <Workflow size={20} /> },
  ];

  // Escape closes the mobile drawer
  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileSidebarOpen]);

  const handleLogout = () => {
    logout();
    addToast(t('messages.logout_success'), 'success');
    navigate('/login', { replace: true });
  };

  const getBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [{ label: t('admin.short'), path: '/admin' }];
    if (pathParts.length > 1) {
      const currentItem = navItems.find((item) => item.path === location.pathname);
      if (currentItem) {
        breadcrumbs.push({ label: currentItem.label, path: location.pathname });
      }
    }
    return breadcrumbs;
  };

  return (
    <div className="flex h-screen bg-gn-tertiary">
      {/* Backdrop behind the mobile drawer (below the z-50 dialogs and toasts) */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          style={{ backgroundColor: 'var(--gn-modal-overlay)' }}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <AdminSidebar
        navItems={navItems}
        activePath={location.pathname}
        userLogin={user?.login}
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-gn-surface border-b border-gn-light flex items-center justify-between gap-2 px-4 md:px-6 shadow-sm">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg text-gn-primary hover:bg-gn-hover"
              aria-label={t('admin.open_menu')}
              aria-controls="admin-sidebar"
              aria-expanded={mobileSidebarOpen}
            >
              <Menu size={20} />
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gn-blue hover:bg-gn-hover rounded-lg transition-colors"
            >
              <MapPin size={18} />
              {t('access.back_to_map')}
            </Link>
          </div>

          <div className="flex items-center gap-2 text-sm text-gn-muted">
            <div className="hidden sm:flex items-center gap-2">
              {getBreadcrumbs().map((breadcrumb, index) => (
                <div key={breadcrumb.path} className="flex items-center gap-2">
                  {index > 0 && <ChevronRight size={16} />}
                  {index === getBreadcrumbs().length - 1 ? (
                    <span className="font-medium text-gn-primary">{breadcrumb.label}</span>
                  ) : (
                    <Link to={breadcrumb.path} className="hover:text-gn-primary">
                      {breadcrumb.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
            <span className="ml-3"><LanguagePicker /></span>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
